import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faUserShield,
  faSearch,
  faEye,
  faCalendar,
  faEnvelope,
  faKey,
  faHistory,
  faUserTag,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisV,
  faDownload,
  faChartLine,
  faLayerGroup,
  faToggleOn,
  faToggleOff,
  faBan,
  faUnlock,
  faDatabase,
  faFileInvoiceDollar,
  faExclamationTriangle,
  faTrash,
  faCheck,
  faTimes,
  faClipboardList,
  faStore,
  faMoneyBillWave,
  faCashRegister,
  faReceipt,
  faArrowUp,
  faArrowDown,
  faTimesCircle,
  faCreditCard,
  faMobileAlt,
  faWallet,
  faTrophy,
  faBalanceScale,
  faChartBar,
  faList,
  faThLarge
} from '@fortawesome/free-solid-svg-icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import api from '../../services/api';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import './GroupsDashboard.css';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const GroupsDashboard = () => {
  const { hasPermission, isSuperAdmin } = useDashboardAuth();
  const canManageGroups = isSuperAdmin() || hasPermission('groups.manage');
  const canViewDataUsage = isSuperAdmin() || hasPermission('groups.data_usage');
  const canViewMembers = isSuperAdmin() || hasPermission('groups.view_members');
  const canBlockGroups = isSuperAdmin() || hasPermission('groups.block');
  const canDeleteGroups = isSuperAdmin() || hasPermission('groups.delete');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive, blocked
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [privilegeAudit, setPrivilegeAudit] = useState([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showDataUsageModal, setShowDataUsageModal] = useState(false);
  const [dataUsage, setDataUsage] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showGestionModal, setShowGestionModal] = useState(false);
  const [gestionReason, setGestionReason] = useState('');
  const [gestionAction, setGestionAction] = useState(null); // 'grant' or 'revoke'
  const [notification, setNotification] = useState(null);
  const [floatingMenu, setFloatingMenu] = useState(null); // { group, x, y }
  const [showPrestationsModal, setShowPrestationsModal] = useState(false);
  const [prestationsReason, setPrestationsReason] = useState('');
  const [prestationsAction, setPrestationsAction] = useState(null);

  // Espace Behavior
  const [showBehaviorModal, setShowBehaviorModal] = useState(false);
  const [behaviorData, setBehaviorData] = useState(null);
  const [behaviorYear, setBehaviorYear] = useState(new Date().getFullYear());
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [behaviorPeriod, setBehaviorPeriod] = useState('year');
  const [behaviorDateFrom, setBehaviorDateFrom] = useState('');
  const [behaviorDateTo, setBehaviorDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // View mode: 'list' (table) or 'card' (modern grid)
  const [groupsViewMode, setGroupsViewMode] = useState('list');

  // Expanded group for mobile actions
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const floatingMenuRef = useRef(null);

  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const toggleGroupExpand = (groupId) => {
    setExpandedGroupId(prev => prev === groupId ? null : groupId);
  };

  const handleRowClick = useCallback((e, group) => {
    setFloatingMenu(prev => {
      if (prev && prev.group.id === group.id) return null;
      const row = e.currentTarget.closest('tr') || e.currentTarget;
      const rect = row.getBoundingClientRect();
      const menuWidth = 270;
      const menuHeight = 420;
      let x = rect.right - menuWidth;
      let y = rect.bottom + 4;
      if (x < 12) x = 12;
      if (x + menuWidth > window.innerWidth - 12) x = window.innerWidth - menuWidth - 12;
      if (y + menuHeight > window.innerHeight) y = rect.top - menuHeight - 4;
      if (y < 12) y = 12;
      return { group, x, y };
    });
  }, []);

  // Check if a group is "new" (created within the last 48 hours)
  const isNewGroup = (group) => {
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return group.created_at && new Date(group.created_at) > cutoff;
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!floatingMenu) return;
    const handleClickOutside = (e) => {
      if (floatingMenuRef.current && !floatingMenuRef.current.contains(e.target)) {
        setFloatingMenu(null);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setFloatingMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [floatingMenu]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/groups');
      // Ensure we have an array and add some mock data if fields are missing for demo
      const data = Array.isArray(response.data) ? response.data.map(g => ({
        ...g,
        status: g.is_blocked ? 'blocked' : (g.status || 'active'), // Mock status if missing
        created_at: g.created_at || new Date().toISOString(), // Mock date if missing
        activity_score: Math.floor(Math.random() * 100) // Mock activity score
      })) : [];
      setGroups(data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const viewGroupMembers = async (group) => {
    try {
      setSelectedGroup(group);
      const response = await api.get(`/admin/groups/${group.id}/members`);
      setGroupMembers(Array.isArray(response.data) ? response.data : []);
      setShowMembersModal(true);
    } catch (error) {
      console.error('Error fetching group members:', error);
      setGroupMembers([]);
    }
  };

  const viewPrivilegeAudit = async (groupId) => {
    try {
      const response = await api.get(`/admin/privileges/audit?groupId=${groupId}`);
      setPrivilegeAudit(Array.isArray(response.data) ? response.data : []);
      setShowAuditModal(true);
    } catch (error) {
      console.error('Error fetching privilege audit:', error);
      setPrivilegeAudit([]);
    }
  };

  const showNotificationMessage = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  // Open modal for gestion access change
  const openGestionModal = (group) => {
    setSelectedGroup(group);
    setGestionAction(group.gestion_access ? 'revoke' : 'grant');
    setGestionReason('');
    setShowGestionModal(true);
  };

  // Submit gestion access request
  const submitGestionRequest = async () => {
    if (!selectedGroup || !gestionReason.trim()) {
      showNotificationMessage('error', 'Veuillez indiquer une raison');
      return;
    }

    try {
      const response = await api.post('/dashboard-admin/gestion-requests', {
        groupId: selectedGroup.id,
        enable: gestionAction === 'grant',
        reason: gestionReason.trim()
      });

      if (response.data.autoApproved) {
        // Super admin or owner - immediate change
        setGroups(groups.map(g => 
          g.id === selectedGroup.id 
            ? { ...g, gestion_access: gestionAction === 'grant' } 
            : g
        ));
        showNotificationMessage('success', 
          gestionAction === 'grant' 
            ? '✅ Accès Gestion accordé' 
            : '✅ Accès Gestion retiré'
        );
      } else {
        showNotificationMessage('success', '📤 Demande envoyée pour approbation');
      }

      setShowGestionModal(false);
      setSelectedGroup(null);
      setGestionReason('');
    } catch (error) {
      showNotificationMessage('error', error.response?.data?.error || 'Erreur lors de la demande');
    }
  };

  // Open modal for prestations access change
  const openPrestationsModal = (group) => {
    setSelectedGroup(group);
    setPrestationsAction(group.prestations_access ? 'revoke' : 'grant');
    setPrestationsReason('');
    setShowPrestationsModal(true);
  };

  // Submit prestations access request
  const submitPrestationsRequest = async () => {
    if (!selectedGroup || !prestationsReason.trim()) {
      showNotificationMessage('error', 'Veuillez indiquer une raison');
      return;
    }

    try {
      const response = await api.post('/dashboard-admin/prestations-requests', {
        groupId: selectedGroup.id,
        enable: prestationsAction === 'grant',
        reason: prestationsReason.trim()
      });

      if (response.data.autoApproved) {
        setGroups(groups.map(g => 
          g.id === selectedGroup.id 
            ? { ...g, prestations_access: prestationsAction === 'grant' } 
            : g
        ));
        showNotificationMessage('success', 
          prestationsAction === 'grant' 
            ? '✅ Accès Prestations accordé' 
            : '✅ Accès Prestations retiré'
        );
      } else {
        showNotificationMessage('success', '📤 Demande envoyée pour approbation');
      }

      setShowPrestationsModal(false);
      setSelectedGroup(null);
      setPrestationsReason('');
    } catch (error) {
      showNotificationMessage('error', error.response?.data?.error || 'Erreur lors de la demande');
    }
  };

  // Block/Unblock Group
  const openBlockModal = (group) => {
    setSelectedGroup(group);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const toggleBlockGroup = async (shouldBlock) => {
    if (!selectedGroup) return;
    
    try {
      await api.put(`/admin/groups/${selectedGroup.id}/block`, { 
        blocked: shouldBlock, 
        reason: blockReason 
      });
      
      // Update local state
      setGroups(groups.map(g => 
        g.id === selectedGroup.id 
          ? { ...g, is_blocked: shouldBlock, block_reason: blockReason, status: shouldBlock ? 'blocked' : 'active' } 
          : g
      ));
      
      setShowBlockModal(false);
      setSelectedGroup(null);
      setBlockReason('');
    } catch (error) {
      console.error('Error toggling group block status:', error);
      alert('Erreur lors de la modification du statut de blocage');
    }
  };

  // Delete Group (soft delete with approval)
  const handleDeleteGroup = async (groupId, groupName) => {
    const reason = window.prompt(
      `Veuillez indiquer la raison de suppression du groupe "${groupName}":\n\n(La demande sera envoyée pour approbation ou appliquée immédiatement selon vos droits)`
    );
    
    if (reason === null) return; // User cancelled
    if (!reason.trim()) {
      alert("La raison est obligatoire pour demander une suppression.");
      return;
    }

    try {
      const response = await api.post('/dashboard-admin/deletion-requests/group', {
        groupId,
        reason: reason.trim()
      });
      
      if (response.data.autoApproved) {
        // Super admin or owner - immediate soft delete
        setGroups(groups.filter(g => g.id !== groupId && g.idgroupe !== groupId));
        if (selectedGroup?.id === groupId || selectedGroup?.idgroupe === groupId) {
          setSelectedGroup(null);
        }
        alert('✅ Groupe supprimé');
      } else {
        // Regular admin - request sent for approval
        alert('📤 Demande de suppression envoyée pour approbation');
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la demande de suppression');
    }
  };

  // Data Usage
  const viewDataUsage = async (group) => {
    try {
      setSelectedGroup(group);
      const q = selectedYear === 'global' ? `?global=true` : `?year=${selectedYear}`;
      const response = await api.get(`/admin/groups/${group.id}/data-usage${q}`);
      setDataUsage(response.data);
      setShowDataUsageModal(true);
    } catch (error) {
      console.error('Error fetching data usage:', error);
      setDataUsage(null);
    }
  };

  const fetchDataUsageForYear = async (year) => {
    if (!selectedGroup) return;
    setSelectedYear(year);
    try {
      const q = year === 'global' ? `?global=true` : `?year=${year}`;
      const response = await api.get(`/admin/groups/${selectedGroup.id}/data-usage${q}`);
      setDataUsage(response.data);
    } catch (error) {
      console.error('Error fetching data usage:', error);
    }
  };

  // Espace Behavior
  const buildBehaviorQuery = (groupId, period, year, dateFrom, dateTo) => {
    let q = `/admin/groups/${groupId}/espace-behavior?period=${period}`;
    if (period === 'year') q += `&year=${year}`;
    if (period === 'custom' && dateFrom && dateTo) q += `&dateFrom=${dateFrom}&dateTo=${dateTo}`;
    return q;
  };

  const viewEspaceBehavior = async (group, year) => {
    try {
      setSelectedGroup(group);
      setBehaviorLoading(true);
      setBehaviorPeriod('year');
      const targetYear = year || behaviorYear;
      const response = await api.get(buildBehaviorQuery(group.id, 'year', targetYear));
      setBehaviorData(response.data);
      setShowBehaviorModal(true);
    } catch (error) {
      console.error('Error fetching espace behavior:', error);
      setBehaviorData(null);
    } finally {
      setBehaviorLoading(false);
    }
  };

  const fetchBehaviorForYear = async (year) => {
    if (!selectedGroup) return;
    setBehaviorYear(year);
    setBehaviorPeriod('year');
    setBehaviorLoading(true);
    try {
      const response = await api.get(buildBehaviorQuery(selectedGroup.id, 'year', year));
      setBehaviorData(response.data);
    } catch (error) {
      console.error('Error fetching behavior:', error);
    } finally {
      setBehaviorLoading(false);
    }
  };

  const fetchBehaviorForPeriod = async (period, customFrom, customTo) => {
    if (!selectedGroup) return;
    setBehaviorPeriod(period);
    setBehaviorLoading(true);
    try {
      const response = await api.get(buildBehaviorQuery(
        selectedGroup.id, period, behaviorYear,
        customFrom || behaviorDateFrom, customTo || behaviorDateTo
      ));
      setBehaviorData(response.data);
    } catch (error) {
      console.error('Error fetching behavior:', error);
    } finally {
      setBehaviorLoading(false);
    }
  };

  const fmtMoney = (v) => (v || 0).toLocaleString('fr-FR');

  // Analytics Data Preparation
  const stats = useMemo(() => {
    const totalGroups = groups.length;
    const totalMembers = groups.reduce((sum, g) => sum + parseInt(g.member_count || 0), 0);
    const activeGroups = groups.filter(g => g.status === 'active').length;
    const blockedGroups = groups.filter(g => g.is_blocked || g.status === 'blocked').length;
    const avgMembers = totalGroups > 0 ? Math.round(totalMembers / totalGroups) : 0;

    return { totalGroups, totalMembers, activeGroups, blockedGroups, avgMembers };
  }, [groups]);

  const chartData = useMemo(() => {
    // Mock growth data based on created_at
    const months = {};
    groups.forEach(g => {
      const date = new Date(g.created_at);
      const key = `${date.getMonth() + 1}/${date.getFullYear()}`;
      months[key] = (months[key] || 0) + 1;
    });
    
    return Object.entries(months).map(([name, count]) => ({ name, count }));
  }, [groups]);

  const sizeDistribution = useMemo(() => {
    let small = 0, medium = 0, large = 0;
    groups.forEach(g => {
      const count = parseInt(g.member_count || 0);
      if (count < 5) small++;
      else if (count < 20) medium++;
      else large++;
    });
    return [
      { name: 'Petit (<5)', value: small },
      { name: 'Moyen (5-20)', value: medium },
      { name: 'Grand (>20)', value: large }
    ];
  }, [groups]);

  // Filtering and Sorting
  const processedGroups = useMemo(() => {
    let result = [...groups];

    // Filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(g => 
        g.nom?.toLowerCase().includes(lower) ||
        g.admin_name?.toLowerCase().includes(lower) ||
        g.admin_email?.toLowerCase().includes(lower)
      );
    }

    if (filterStatus !== 'all') {
      if (filterStatus === 'blocked') {
        result = result.filter(g => g.is_blocked || g.status === 'blocked');
      } else {
        result = result.filter(g => g.status === filterStatus && !g.is_blocked);
      }
    }

    // Date range filter
    if (dateFrom || dateTo) {
      result = result.filter(g => {
        const created = new Date(g.created_at);
        created.setHours(0, 0, 0, 0);
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          if (created < from) return false;
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          if (created > to) return false;
        }
        return true;
      });
    }

    // Sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [groups, searchTerm, filterStatus, dateFrom, dateTo, sortConfig]);

  // Pagination Logic
  const totalPages = Math.ceil(processedGroups.length / itemsPerPage);
  const paginatedGroups = processedGroups.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="groups-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Gestion des Groupes</h1>
          <p>Vue d'ensemble et administration des espaces groupes</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary">
            <FontAwesomeIcon icon={faDownload} /> Exporter
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon bg-indigo">
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div className="stat-info">
            <h3>Total Groupes</h3>
            <p className="stat-value">{stats.totalGroups}</p>
            <span className="stat-trend positive">+12% ce mois</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon bg-emerald">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="stat-info">
            <h3>Total Membres</h3>
            <p className="stat-value">{stats.totalMembers}</p>
            <span className="stat-trend positive">+5% ce mois</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon bg-amber">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="stat-info">
            <h3>Moyenne Membres</h3>
            <p className="stat-value">{stats.avgMembers}</p>
            <span className="stat-trend neutral">Stable</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon bg-red">
            <FontAwesomeIcon icon={faBan} />
          </div>
          <div className="stat-info">
            <h3>Groupes Bloqués</h3>
            <p className="stat-value">{stats.blockedGroups}</p>
            <span className="stat-trend neutral">{stats.totalGroups > 0 ? ((stats.blockedGroups / stats.totalGroups) * 100).toFixed(1) : 0}% du total</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-card">
          <h3>Croissance des Groupes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
              <XAxis dataKey="name" stroke="var(--text-secondary)" />
              <YAxis stroke="var(--text-secondary)" />
              <Tooltip 
                contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
              <Area type="monotone" dataKey="count" stroke="#6366f1" fillOpacity={1} fill="url(#colorCount)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <h3>Taille des Groupes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sizeDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {sizeDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Main Content */}
      <div className="content-card">
        <div className="card-header">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Rechercher un groupe, admin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filters">
            <div className="select-wrapper">
              <FontAwesomeIcon icon={faFilter} />
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
                <option value="blocked">Bloqués</option>
              </select>
            </div>
          </div>
          <div className="groups-view-toggle">
            <button
              className={`groups-view-toggle-btn ${groupsViewMode === 'list' ? 'active' : ''}`}
              onClick={() => setGroupsViewMode('list')}
            >
              <FontAwesomeIcon icon={faList} /> Liste
            </button>
            <button
              className={`groups-view-toggle-btn ${groupsViewMode === 'card' ? 'active' : ''}`}
              onClick={() => setGroupsViewMode('card')}
            >
              <FontAwesomeIcon icon={faThLarge} /> Cartes
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="date-range-filter">
          <FontAwesomeIcon icon={faCalendar} className="date-filter-icon" />
          <span className="date-range-label">Période :</span>
          <div className="date-range-inputs">
            <input
              type="date"
              className="date-filter-input"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
              title="Date de début"
            />
            <span className="date-range-separator">→</span>
            <input
              type="date"
              className="date-filter-input"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
              title="Date de fin"
            />
          </div>
          {(dateFrom || dateTo) && (
            <>
              <span className="date-range-count">{processedGroups.length} résultat{processedGroups.length !== 1 ? 's' : ''}</span>
              <button className="date-filter-clear" onClick={() => { setDateFrom(''); setDateTo(''); }} title="Effacer le filtre">×</button>
            </>
          )}
        </div>

        {groupsViewMode === 'list' ? (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('nom')} className="sortable">
                  Groupe {sortConfig.key === 'nom' && <FontAwesomeIcon icon={sortConfig.direction === 'asc' ? faSortUp : faSortDown} />}
                </th>
                <th onClick={() => handleSort('admin_name')} className="sortable">
                  Administrateur {sortConfig.key === 'admin_name' && <FontAwesomeIcon icon={sortConfig.direction === 'asc' ? faSortUp : faSortDown} />}
                </th>
                <th onClick={() => handleSort('member_count')} className="sortable">
                  Membres {sortConfig.key === 'member_count' && <FontAwesomeIcon icon={sortConfig.direction === 'asc' ? faSortUp : faSortDown} />}
                </th>
                <th onClick={() => handleSort('created_at')} className="sortable">
                  Création {sortConfig.key === 'created_at' && <FontAwesomeIcon icon={sortConfig.direction === 'asc' ? faSortUp : faSortDown} />}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedGroups.map(group => (
                <React.Fragment key={group.id}>
                <tr className={`${group.is_blocked ? 'blocked-row' : ''} ${floatingMenu?.group?.id === group.id ? 'expanded-row' : ''}`} onClick={(e) => handleRowClick(e, group)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="group-info">
                      <div className={`group-avatar ${group.is_blocked ? 'blocked' : ''}`}>
                        {group.is_blocked ? <FontAwesomeIcon icon={faBan} /> : group.nom.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <span className="group-name">
                          {group.nom}
                          {group.is_blocked && <span className="blocked-badge">Bloqué</span>}
                          {!group.is_blocked && isNewGroup(group) && <span className="nouveau-tag">Nouveau</span>}
                        </span>
                        <span className="group-id">ID: {group.id}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-info">
                      <span>{group.admin_name}</span>
                      <small>{group.admin_email}</small>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      <FontAwesomeIcon icon={faUsers} /> {group.member_count}
                    </span>
                  </td>
                  <td>
                    {new Date(group.created_at).toLocaleDateString()}
                  </td>
                  <td className="actions-desktop-cell">
                    <div className="actions-cell">
                      <button
                        className="btn-icon btn-more-actions"
                        onClick={(e) => { e.stopPropagation(); handleRowClick(e, group); }}
                        title="Actions"
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </button>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="groups-card-grid">
          {paginatedGroups.map(group => (
            <div
              key={group.id}
              className={`group-card ${group.is_blocked ? 'blocked' : ''} ${floatingMenu?.group?.id === group.id ? 'active' : ''}`}
              onClick={(e) => handleRowClick(e, group)}
            >
              <div className="group-card-header">
                <div className={`group-avatar ${group.is_blocked ? 'blocked' : ''}`}>
                  {group.is_blocked ? <FontAwesomeIcon icon={faBan} /> : group.nom.substring(0, 2).toUpperCase()}
                </div>
                <div className="group-card-title">
                  <span className="group-name" title={group.nom}>{group.nom}</span>
                  <span className="group-id">ID: {group.id}</span>
                </div>
                <button
                  className="btn-icon btn-more-actions"
                  onClick={(e) => { e.stopPropagation(); handleRowClick(e, group); }}
                  title="Actions"
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
              </div>

              <div className="group-card-badges">
                {group.is_blocked && <span className="blocked-badge">Bloqué</span>}
                {!group.is_blocked && isNewGroup(group) && <span className="nouveau-tag">Nouveau</span>}
                <span className="badge badge-info">
                  <FontAwesomeIcon icon={faUsers} /> {group.member_count} membre{group.member_count !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="group-card-body">
                <div className="group-card-row">
                  <span className="group-card-label">Administrateur</span>
                  <div className="admin-info">
                    <span>{group.admin_name}</span>
                    <small>{group.admin_email}</small>
                  </div>
                </div>
                <div className="group-card-row">
                  <span className="group-card-label">Créé le</span>
                  <span className="group-card-value">{new Date(group.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Floating Action Menu */}
        {floatingMenu && (
          <div className="floating-menu-backdrop" onClick={() => setFloatingMenu(null)}>
            <div 
              ref={floatingMenuRef}
              className="floating-action-menu"
              style={{ top: floatingMenu.y, left: floatingMenu.x }}
              onClick={e => e.stopPropagation()}
            >
              <div className="floating-menu-header">
                <div className="floating-menu-group-avatar">
                  {floatingMenu.group.nom.substring(0, 2).toUpperCase()}
                </div>
                <div className="floating-menu-group-info">
                  <span className="floating-menu-group-name">{floatingMenu.group.nom}</span>
                  <span className="floating-menu-group-id">ID: {floatingMenu.group.id}</span>
                </div>
                <button className="floating-menu-close" onClick={() => setFloatingMenu(null)}>&times;</button>
              </div>

              <div className="floating-menu-section">
                <span className="floating-menu-section-label">Accès</span>
                {canManageGroups && (
                  <button className="floating-menu-item" onClick={() => { openGestionModal(floatingMenu.group); setFloatingMenu(null); }}>
                    <FontAwesomeIcon 
                      icon={floatingMenu.group.gestion_access ? faToggleOn : faToggleOff} 
                      className={floatingMenu.group.gestion_access ? 'toggle-active' : 'toggle-inactive'} 
                    />
                    <span className="floating-menu-item-label">Gestion</span>
                    <span className={`floating-menu-item-badge ${floatingMenu.group.gestion_access ? 'badge-active' : 'badge-inactive'}`}>
                      {floatingMenu.group.gestion_access ? 'Actif' : 'Inactif'}
                    </span>
                  </button>
                )}
                {canManageGroups && (
                  <button className="floating-menu-item" onClick={() => { openPrestationsModal(floatingMenu.group); setFloatingMenu(null); }}>
                    <FontAwesomeIcon 
                      icon={floatingMenu.group.prestations_access ? faToggleOn : faToggleOff} 
                      className={floatingMenu.group.prestations_access ? 'toggle-active' : 'toggle-inactive'} 
                    />
                    <span className="floating-menu-item-label">Prestations</span>
                    <span className={`floating-menu-item-badge ${floatingMenu.group.prestations_access ? 'badge-active' : 'badge-inactive'}`}>
                      {floatingMenu.group.prestations_access ? 'Actif' : 'Inactif'}
                    </span>
                  </button>
                )}
              </div>

              <div className="floating-menu-section">
                <span className="floating-menu-section-label">Actions</span>
                {canBlockGroups && (
                  <button className="floating-menu-item" onClick={() => { openBlockModal(floatingMenu.group); setFloatingMenu(null); }}>
                    <FontAwesomeIcon 
                      icon={floatingMenu.group.is_blocked ? faUnlock : faBan} 
                      className={floatingMenu.group.is_blocked ? 'toggle-active' : 'icon-danger'} 
                    />
                    <span className="floating-menu-item-label">
                      {floatingMenu.group.is_blocked ? 'Débloquer' : 'Bloquer'}
                    </span>
                  </button>
                )}
                {canViewDataUsage && (
                  <button className="floating-menu-item" onClick={() => { viewDataUsage(floatingMenu.group); setFloatingMenu(null); }}>
                    <FontAwesomeIcon icon={faDatabase} />
                    <span className="floating-menu-item-label">Consommation données</span>
                  </button>
                )}
                {canViewDataUsage && (
                  <button className="floating-menu-item" onClick={() => { viewEspaceBehavior(floatingMenu.group); setFloatingMenu(null); }}>
                    <FontAwesomeIcon icon={faStore} className="icon-behavior" />
                    <span className="floating-menu-item-label">Activité Espace</span>
                    <span className="floating-menu-item-badge badge-active">Nouveau</span>
                  </button>
                )}
                {canViewMembers && (
                  <button className="floating-menu-item" onClick={() => { viewGroupMembers(floatingMenu.group); setFloatingMenu(null); }}>
                    <FontAwesomeIcon icon={faUsers} />
                    <span className="floating-menu-item-label">Membres</span>
                  </button>
                )}
                <button className="floating-menu-item" onClick={() => { viewPrivilegeAudit(floatingMenu.group.id); setFloatingMenu(null); }}>
                  <FontAwesomeIcon icon={faHistory} />
                  <span className="floating-menu-item-label">Audit privilèges</span>
                </button>
              </div>

              {canDeleteGroups && (
                <div className="floating-menu-section floating-menu-section-danger">
                  <button className="floating-menu-item floating-menu-item-danger" onClick={() => { handleDeleteGroup(floatingMenu.group.id || floatingMenu.group.idgroupe, floatingMenu.group.name || floatingMenu.group.nom); setFloatingMenu(null); }}>
                    <FontAwesomeIcon icon={faTrash} />
                    <span className="floating-menu-item-label">Supprimer le groupe</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination">
          <span>Affichage {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, processedGroups.length)} sur {processedGroups.length}</span>
          <div className="pagination-controls">
            <button 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Précédent
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={currentPage === i + 1 ? 'active' : ''}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {/* Modals would go here - keeping existing logic for them but simplified for this example */}
      {showMembersModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowMembersModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Membres - {selectedGroup.nom}</h2>
              <button className="close-btn" onClick={() => setShowMembersModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Rôle</th>
                  </tr>
                </thead>
                <tbody>
                  {groupMembers.map(member => (
                    <tr key={member.id}>
                      <td>{member.nom} {member.prenom}</td>
                      <td>{member.email}</td>
                      <td>{member.role || 'Membre'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Audit Modal */}
      {showAuditModal && (
        <div className="modal-overlay" onClick={() => setShowAuditModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit des Privilèges</h2>
              <button className="close-btn" onClick={() => setShowAuditModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {privilegeAudit.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Privilège</th>
                      <th>Utilisateur cible</th>
                      <th>Effectué par</th>
                      <th>Raison</th>
                    </tr>
                  </thead>
                  <tbody>
                    {privilegeAudit.map((audit, index) => (
                      <tr key={index}>
                        <td>{new Date(audit.created_at || audit.timestamp || Date.now()).toLocaleString('fr-FR')}</td>
                        <td>
                          <span className={`action-badge ${audit.action === 'grant' ? 'grant' : 'revoke'}`}>
                            {audit.action === 'grant' ? 'Accordé' : audit.action === 'revoke' ? 'Retiré' : audit.action}
                          </span>
                        </td>
                        <td>{audit.privilege_name || audit.role_to || 'N/A'}</td>
                        <td>
                          <div className="user-cell">
                            <span>{audit.target_user}</span>
                            <small>{audit.target_email}</small>
                          </div>
                        </td>
                        <td>
                          <div className="user-cell">
                            <span>{audit.performed_by_name}</span>
                            <small>{audit.performed_by_email}</small>
                          </div>
                        </td>
                        <td>{audit.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <p>Aucun historique de privilèges disponible pour ce groupe.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Block Group Modal */}
      {showBlockModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={selectedGroup.is_blocked ? faUnlock : faBan} style={{ marginRight: '0.5rem' }} />
                {selectedGroup.is_blocked ? 'Débloquer' : 'Bloquer'} le groupe
              </h2>
              <button className="close-btn" onClick={() => setShowBlockModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="block-modal-content">
                <div className="group-preview">
                  <div className={`group-avatar large ${selectedGroup.is_blocked ? 'blocked' : ''}`}>
                    {selectedGroup.nom.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3>{selectedGroup.nom}</h3>
                    <p>{selectedGroup.admin_email}</p>
                    <span className="member-count">{selectedGroup.member_count} membres</span>
                  </div>
                </div>
                
                {selectedGroup.is_blocked ? (
                  <div className="unblock-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <p>Ce groupe est actuellement bloqué{selectedGroup.block_reason ? ` pour: "${selectedGroup.block_reason}"` : ''}. Voulez-vous le débloquer ?</p>
                  </div>
                ) : (
                  <div className="block-form">
                    <label>Raison du blocage (optionnel)</label>
                    <textarea
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Ex: Non-paiement, Violation des conditions d'utilisation..."
                      rows="3"
                    />
                    <div className="block-warning">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <p>Bloquer ce groupe empêchera tous ses membres d'y accéder.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowBlockModal(false)}>
                Annuler
              </button>
              <button 
                className={selectedGroup.is_blocked ? 'btn-success' : 'btn-danger'}
                onClick={() => toggleBlockGroup(!selectedGroup.is_blocked)}
              >
                <FontAwesomeIcon icon={selectedGroup.is_blocked ? faUnlock : faBan} />
                {selectedGroup.is_blocked ? ' Débloquer' : ' Bloquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Usage Modal — Data Only */}
      {showDataUsageModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowDataUsageModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faDatabase} style={{ marginRight: '0.5rem' }} />
                Consommation de données - {selectedGroup.nom}
              </h2>
              <button className="close-btn" onClick={() => setShowDataUsageModal(false)}>&times;</button>
            </div>
            <div className="modal-body modal-body-scroll">
              <div className="data-usage-header">
                <div className="year-selector">
                  <label>Période:</label>
                  <select 
                    value={selectedYear} 
                    onChange={(e) => {
                      const val = e.target.value;
                      fetchDataUsageForYear(val === 'global' ? 'global' : parseInt(val));
                    }}
                  >
                    <option value="global">🌍 Global (tout)</option>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
                <button className="btn-primary">
                  <FontAwesomeIcon icon={faFileInvoiceDollar} /> Générer Facture
                </button>
              </div>

              {dataUsage ? (
                <>
                  {/* Data Summary Cards */}
                  <div className="data-usage-summary">
                    <div className="usage-stat-card highlight">
                      <h4>Total Données</h4>
                      <p className="usage-value">{dataUsage.yearly.totalDataMB} MB</p>
                      <span className="usage-detail">{dataUsage.yearly.totalDataGB} GB</span>
                    </div>
                    <div className="usage-stat-card">
                      <h4>Enregistrements</h4>
                      <p className="usage-value">{(dataUsage.yearly.totalRecords || 0).toLocaleString('fr-FR')}</p>
                      <span className="usage-detail">Toutes tables confondues</span>
                    </div>
                    <div className="usage-stat-card">
                      <h4>Fichiers & Médias</h4>
                      <p className="usage-value">{(dataUsage.yearly.totalFiles || 0).toLocaleString('fr-FR')}</p>
                      <span className="usage-detail">{((dataUsage.yearly.totalFileBytes || 0) / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>
                    <div className="usage-stat-card">
                      <h4>Membres</h4>
                      <p className="usage-value">{dataUsage.yearly.activeMembers || 0}/{dataUsage.yearly.totalMembers || 0}</p>
                      <span className="usage-detail">{dataUsage.yearly.totalMembers > 0 ? ((dataUsage.yearly.activeMembers / dataUsage.yearly.totalMembers) * 100).toFixed(0) : 0}% actifs</span>
                    </div>
                  </div>

                  {/* Full Data Breakdown by Category */}
                  {dataUsage.breakdown && (
                    <div className="data-breakdown-section">
                      <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faDatabase} /> Détail par Type de Données
                      </h4>
                      <div className="data-breakdown-grid">
                        {Object.entries(dataUsage.breakdown)
                          .filter(([, val]) => val.count > 0)
                          .sort((a, b) => b[1].bytes - a[1].bytes)
                          .map(([key, val]) => {
                            const pct = dataUsage.yearly.totalRecords > 0
                              ? ((val.bytes / Object.values(dataUsage.breakdown).reduce((s, d) => s + d.bytes, 0)) * 100).toFixed(1)
                              : 0;
                            return (
                              <div key={key} className="data-breakdown-row">
                                <div className="db-info">
                                  <span className="db-label">{val.label}</span>
                                  <span className="db-count">{val.count.toLocaleString('fr-FR')} enregistrements</span>
                                </div>
                                <div className="db-bar-wrap">
                                  <div className="db-bar-fill" style={{ width: `${Math.min(pct, 100)}%` }}></div>
                                </div>
                                <div className="db-size">
                                  <span className="db-bytes">{(val.bytes / (1024 * 1024)).toFixed(2)} MB</span>
                                  <span className="db-pct">{pct}%</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      {/* Empty categories summary */}
                      {(() => {
                        const emptyCount = Object.values(dataUsage.breakdown).filter(v => v.count === 0).length;
                        return emptyCount > 0 ? (
                          <p className="empty-categories-note">
                            + {emptyCount} catégorie{emptyCount > 1 ? 's' : ''} sans données (
                            {Object.entries(dataUsage.breakdown).filter(([, v]) => v.count === 0).map(([, v]) => v.label).join(', ')})
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Monthly Data Volume Chart */}
                  <div className="data-usage-charts-grid">
                    <div className="data-usage-chart" style={{ gridColumn: '1 / -1' }}>
                      <h4>Volume de Données Mensuel ({selectedYear})</h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={dataUsage.monthly}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                          <XAxis dataKey="monthName" stroke="var(--text-secondary)" fontSize={11} />
                          <YAxis stroke="var(--text-secondary)" fontSize={11} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                            formatter={(value, name) => {
                              if (name === 'dataMB') return [`${value} MB`, 'Données'];
                              if (name === 'messages') return [value.toLocaleString('fr-FR'), 'Messages'];
                              if (name === 'files') return [value.toLocaleString('fr-FR'), 'Fichiers'];
                              if (name === 'ventes') return [value.toLocaleString('fr-FR'), 'Ventes'];
                              if (name === 'charges') return [value.toLocaleString('fr-FR'), 'Charges'];
                              if (name === 'caisses') return [value.toLocaleString('fr-FR'), 'Caisses'];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="dataMB" name="Données (MB)" fill="#6366f1" />
                          <Bar dataKey="messages" name="Messages" fill="#10b981" />
                          <Bar dataKey="ventes" name="Ventes" fill="#f59e0b" />
                          <Bar dataKey="charges" name="Charges" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Monthly Detail Table */}
                  <div className="data-usage-table">
                    <h4>Détail Mensuel</h4>
                    <div className="table-responsive">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Mois</th>
                            <th>Messages</th>
                            <th>Fichiers</th>
                            <th>Ventes</th>
                            <th>Charges</th>
                            <th>Caisses</th>
                            <th>Données</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dataUsage.monthly.map(month => (
                            <tr key={month.month}>
                              <td><strong>{month.monthName}</strong></td>
                              <td>{(month.messages || 0).toLocaleString('fr-FR')}</td>
                              <td>{(month.files || 0).toLocaleString('fr-FR')}</td>
                              <td>{(month.ventes || 0).toLocaleString('fr-FR')}</td>
                              <td>{(month.charges || 0).toLocaleString('fr-FR')}</td>
                              <td>{(month.caisses || 0).toLocaleString('fr-FR')}</td>
                              <td>{month.dataMB} MB</td>
                            </tr>
                          ))}
                          <tr className="total-row">
                            <td><strong>TOTAL</strong></td>
                            <td><strong>{(dataUsage.yearly.totalMessages || 0).toLocaleString('fr-FR')}</strong></td>
                            <td><strong>{(dataUsage.yearly.totalFiles || 0).toLocaleString('fr-FR')}</strong></td>
                            <td><strong>{(dataUsage.yearly.totalVentes || 0).toLocaleString('fr-FR')}</strong></td>
                            <td><strong>{(dataUsage.yearly.totalCharges || 0).toLocaleString('fr-FR')}</strong></td>
                            <td><strong>{(dataUsage.yearly.totalCaisses || 0).toLocaleString('fr-FR')}</strong></td>
                            <td><strong>{dataUsage.yearly.totalDataMB} MB</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>Chargement des données de consommation...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Activité Espace Modal ═══ */}
      {showBehaviorModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowBehaviorModal(false)}>
          <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header behavior-header">
              <h2>
                <FontAwesomeIcon icon={faStore} style={{ marginRight: '0.5rem', color: '#8b5cf6' }} />
                Activité Espace - {selectedGroup.nom}
              </h2>
              <button className="close-btn" onClick={() => setShowBehaviorModal(false)}>&times;</button>
            </div>
            <div className="modal-body modal-body-scroll">
              <div className="behavior-period-bar">
                <div className="period-buttons">
                  <button className={`period-btn ${behaviorPeriod === 'today' ? 'active' : ''}`} onClick={() => fetchBehaviorForPeriod('today')}>Aujourd'hui</button>
                  <button className={`period-btn ${behaviorPeriod === '7days' ? 'active' : ''}`} onClick={() => fetchBehaviorForPeriod('7days')}>7 derniers jours</button>
                  <button className={`period-btn ${behaviorPeriod === 'month' ? 'active' : ''}`} onClick={() => fetchBehaviorForPeriod('month')}>Ce mois</button>
                  <button className={`period-btn ${behaviorPeriod === 'year' ? 'active' : ''}`} onClick={() => fetchBehaviorForPeriod('year')}>
                    Année
                  </button>
                  {behaviorPeriod === 'year' && (
                    <select className="period-year-select" value={behaviorYear} onChange={(e) => fetchBehaviorForYear(parseInt(e.target.value))}>
                      {[...Array(5)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
                    </select>
                  )}
                  <button className={`period-btn ${behaviorPeriod === 'custom' ? 'active' : ''}`} onClick={() => setBehaviorPeriod('custom')}>Personnalisé</button>
                </div>
                {behaviorPeriod === 'custom' && (
                  <div className="period-custom-dates">
                    <label>Du</label>
                    <input type="date" value={behaviorDateFrom} onChange={(e) => setBehaviorDateFrom(e.target.value)} />
                    <label>Au</label>
                    <input type="date" value={behaviorDateTo} onChange={(e) => setBehaviorDateTo(e.target.value)} />
                    <button className="period-btn active" onClick={() => { if (behaviorDateFrom && behaviorDateTo) fetchBehaviorForPeriod('custom', behaviorDateFrom, behaviorDateTo); }} disabled={!behaviorDateFrom || !behaviorDateTo}>Appliquer</button>
                  </div>
                )}
              </div>

              {behaviorLoading ? (
                <div className="empty-state"><div className="spinner"></div><p>Chargement...</p></div>
              ) : behaviorData ? (
                <>
                  {/* ── Row 1: Financial Overview ── */}
                  <div className="behavior-section-title"><FontAwesomeIcon icon={faChartBar} /> Vue Financière</div>
                  <div className="behavior-cards-grid">
                    <div className="behavior-card card-ca">
                      <div className="bcard-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                      <div className="bcard-info">
                        <span className="bcard-label">CA Net</span>
                        <span className="bcard-value">{fmtMoney(behaviorData.ventes?.ca_net)} FCFA</span>
                        <span className="bcard-sub">Hors ventes annulées</span>
                      </div>
                    </div>
                    <div className="behavior-card card-encaisse">
                      <div className="bcard-icon" style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}><FontAwesomeIcon icon={faCashRegister} /></div>
                      <div className="bcard-info">
                        <span className="bcard-label">Encaissé</span>
                        <span className="bcard-value">{fmtMoney(behaviorData.ventes?.total_encaisse)} FCFA</span>
                        <span className="bcard-sub">Paiements reçus</span>
                      </div>
                    </div>
                    <div className="behavior-card card-charges">
                      <div className="bcard-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}><FontAwesomeIcon icon={faReceipt} /></div>
                      <div className="bcard-info">
                        <span className="bcard-label">Charges</span>
                        <span className="bcard-value">{fmtMoney(behaviorData.charges?.total)} FCFA</span>
                        <span className="bcard-sub">{behaviorData.charges?.count || 0} dépenses</span>
                      </div>
                    </div>
                    <div className="behavior-card card-benefice">
                      <div className="bcard-icon" style={{ background: behaviorData.resultat?.benefice_brut >= 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: behaviorData.resultat?.benefice_brut >= 0 ? '#10b981' : '#ef4444' }}>
                        <FontAwesomeIcon icon={faBalanceScale} />
                      </div>
                      <div className="bcard-info">
                        <span className="bcard-label">Résultat Brut</span>
                        <span className={`bcard-value ${behaviorData.resultat?.benefice_brut >= 0 ? 'positive' : 'negative'}`}>
                          {behaviorData.resultat?.benefice_brut >= 0 ? '+' : ''}{fmtMoney(behaviorData.resultat?.benefice_brut)} FCFA
                        </span>
                        <span className="bcard-sub">CA - Charges</span>
                      </div>
                    </div>
                  </div>

                  {/* ── Row 2: Ventes Breakdown ── */}
                  <div className="behavior-section-title"><FontAwesomeIcon icon={faChartLine} /> Détail Ventes</div>
                  <div className="behavior-cards-grid five-cols">
                    <div className="behavior-card mini">
                      <span className="bcard-mini-label">Total Ventes</span>
                      <span className="bcard-mini-value">{behaviorData.ventes?.total || 0}</span>
                    </div>
                    <div className="behavior-card mini card-success">
                      <span className="bcard-mini-label">✅ Payées</span>
                      <span className="bcard-mini-value">{behaviorData.ventes?.paid || 0}</span>
                    </div>
                    <div className="behavior-card mini card-warning">
                      <span className="bcard-mini-label">⏳ En attente</span>
                      <span className="bcard-mini-value">{behaviorData.ventes?.pending || 0}</span>
                    </div>
                    <div className="behavior-card mini card-partial">
                      <span className="bcard-mini-label">🔶 Partielles</span>
                      <span className="bcard-mini-value">{behaviorData.ventes?.partial || 0}</span>
                    </div>
                    <div className="behavior-card mini card-danger">
                      <span className="bcard-mini-label">❌ Annulées</span>
                      <span className="bcard-mini-value">{behaviorData.ventes?.cancelled || 0}</span>
                      {behaviorData.ventes?.ca_cancelled > 0 && (
                        <span className="bcard-mini-sub">-{fmtMoney(behaviorData.ventes.ca_cancelled)} FCFA</span>
                      )}
                    </div>
                  </div>

                  {/* ── Row 3: Panier Moyen + Restant + Avoirs ── */}
                  <div className="behavior-cards-grid three-cols">
                    <div className="behavior-card mini">
                      <span className="bcard-mini-label">🛒 Panier Moyen</span>
                      <span className="bcard-mini-value">{fmtMoney(behaviorData.ventes?.avg_basket)} FCFA</span>
                    </div>
                    <div className="behavior-card mini card-warning">
                      <span className="bcard-mini-label">💰 Restant à encaisser</span>
                      <span className="bcard-mini-value">{fmtMoney(behaviorData.ventes?.total_restant)} FCFA</span>
                    </div>
                    <div className="behavior-card mini">
                      <span className="bcard-mini-label">🔄 Avoirs Total</span>
                      <span className="bcard-mini-value">{fmtMoney(behaviorData.avoirs?.grand_total)} FCFA</span>
                      <span className="bcard-mini-sub">Soldés: {fmtMoney((behaviorData.avoirs?.ventes?.solded || 0) + (behaviorData.avoirs?.prestations?.solded || 0))}</span>
                    </div>
                  </div>

                  {/* ── Payment Methods & Charges Categories side by side ── */}
                  <div className="behavior-two-cols">
                    <div className="behavior-panel">
                      <h4><FontAwesomeIcon icon={faCreditCard} /> Modes de Paiement</h4>
                      {behaviorData.paymentMethods?.length > 0 ? (
                        <div className="payment-methods-list">
                          {behaviorData.paymentMethods.map((pm, i) => {
                            const icons = { cash: faWallet, mobile: faMobileAlt, carte: faCreditCard };
                            const colors = { cash: '#10b981', mobile: '#f59e0b', carte: '#6366f1' };
                            const pct = behaviorData.ventes?.total_encaisse > 0 ? ((pm.total / behaviorData.ventes.total_encaisse) * 100).toFixed(1) : 0;
                            return (
                              <div key={i} className="pm-row">
                                <div className="pm-icon" style={{ color: colors[pm.method] || '#8b5cf6' }}>
                                  <FontAwesomeIcon icon={icons[pm.method] || faMoneyBillWave} />
                                </div>
                                <div className="pm-info">
                                  <span className="pm-name">{pm.method}</span>
                                  <div className="pm-bar">
                                    <div className="pm-bar-fill" style={{ width: `${pct}%`, background: colors[pm.method] || '#8b5cf6' }}></div>
                                  </div>
                                </div>
                                <div className="pm-amount">
                                  <span className="pm-total">{fmtMoney(pm.total)}</span>
                                  <span className="pm-pct">{pct}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <p className="empty-text">Aucun paiement enregistré</p>}
                    </div>

                    <div className="behavior-panel">
                      <h4><FontAwesomeIcon icon={faReceipt} /> Charges par Catégorie</h4>
                      {behaviorData.charges?.categories?.length > 0 ? (
                        <div className="charges-cat-list">
                          {behaviorData.charges.categories.map((cat, i) => {
                            const pct = behaviorData.charges?.total > 0 ? ((cat.total / behaviorData.charges.total) * 100).toFixed(1) : 0;
                            return (
                              <div key={i} className="cat-row">
                                <span className="cat-name">{cat.name}</span>
                                <div className="cat-bar">
                                  <div className="cat-bar-fill" style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="cat-total">{fmtMoney(cat.total)}</span>
                                <span className="cat-pct">{pct}%</span>
                              </div>
                            );
                          })}
                          <div className="charges-type-split">
                            <span>Fixes: <strong>{fmtMoney(behaviorData.charges.fixe)}</strong></span>
                            <span>Variables: <strong>{fmtMoney(behaviorData.charges.variable)}</strong></span>
                          </div>
                        </div>
                      ) : <p className="empty-text">Aucune charge enregistrée</p>}
                    </div>
                  </div>

                  {/* ── Top Products ── */}
                  {behaviorData.topProducts?.length > 0 && (
                    <div className="behavior-panel full-width">
                      <h4><FontAwesomeIcon icon={faTrophy} /> Top 5 Produits</h4>
                      <div className="top-products-list">
                        {behaviorData.topProducts.map((p, i) => (
                          <div key={i} className="top-product-row">
                            <span className="tp-rank">#{i + 1}</span>
                            <span className="tp-name">{p.name}</span>
                            <span className="tp-qty">{p.qty} vendus</span>
                            <span className="tp-revenue">{fmtMoney(p.revenue)} FCFA</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Caisses Summary ── */}
                  <div className="behavior-section-title"><FontAwesomeIcon icon={faCashRegister} /> Caisses</div>
                  <div className="behavior-cards-grid five-cols">
                    <div className="behavior-card mini">
                      <span className="bcard-mini-label">Sessions</span>
                      <span className="bcard-mini-value">{behaviorData.caisses?.total_sessions || 0}</span>
                    </div>
                    <div className="behavior-card mini card-success">
                      <span className="bcard-mini-label">Fermées</span>
                      <span className="bcard-mini-value">{behaviorData.caisses?.closed || 0}</span>
                    </div>
                    <div className="behavior-card mini card-warning">
                      <span className="bcard-mini-label">Ouvertes</span>
                      <span className="bcard-mini-value">{behaviorData.caisses?.open || 0}</span>
                    </div>
                    <div className={`behavior-card mini ${behaviorData.caisses?.net_ecart > 0 ? 'card-success' : behaviorData.caisses?.net_ecart < 0 ? 'card-danger' : ''}`}>
                      <span className="bcard-mini-label">⚖️ Écart</span>
                      <span className="bcard-mini-value" style={{ color: behaviorData.caisses?.net_ecart > 0 ? '#10b981' : behaviorData.caisses?.net_ecart < 0 ? '#ef4444' : 'inherit' }}>
                        {behaviorData.caisses?.net_ecart > 0 ? '+' : ''}{fmtMoney(behaviorData.caisses?.net_ecart || 0)} FCFA
                      </span>
                      {behaviorData.caisses?.total_ecart > 0 && (
                        <span className="bcard-mini-sub">Abs: {fmtMoney(behaviorData.caisses.total_ecart)} FCFA</span>
                      )}
                    </div>
                    <div className="behavior-card mini card-danger">
                      <span className="bcard-mini-label">Écarts non résolus</span>
                      <span className="bcard-mini-value">{behaviorData.caisses?.ecarts_non_resolus || 0}</span>
                    </div>
                  </div>

                  {/* ── Monthly Trends Chart ── */}
                  <div className="behavior-section-title"><FontAwesomeIcon icon={faChartBar} /> Tendances Mensuelles</div>
                  <div className="data-usage-charts-grid">
                    <div className="data-usage-chart">
                      <h4>CA vs Charges ({behaviorYear})</h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={behaviorData.monthly}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                          <XAxis dataKey="monthName" stroke="var(--text-secondary)" fontSize={11} />
                          <YAxis stroke="var(--text-secondary)" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} formatter={(v, name) => [`${(v||0).toLocaleString('fr-FR')} FCFA`, name]} />
                          <Legend />
                          <Bar dataKey="ca_net" name="CA Net" fill="#10b981" />
                          <Bar dataKey="charges" name="Charges" fill="#ef4444" />
                          <Bar dataKey="encaisse" name="Encaissé" fill="#6366f1" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="data-usage-chart">
                      <h4>Ventes: Valides vs Annulées ({behaviorYear})</h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={behaviorData.monthly}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                          <XAxis dataKey="monthName" stroke="var(--text-secondary)" fontSize={11} />
                          <YAxis stroke="var(--text-secondary)" fontSize={11} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                          <Legend />
                          <Bar dataKey="ventes_valid" name="Valides" fill="#10b981" stackId="a" />
                          <Bar dataKey="ventes_cancelled" name="Annulées" fill="#ef4444" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* ── Monthly Detail Table ── */}
                  <div className="data-usage-table">
                    <h4>Résumé Mensuel</h4>
                    <div className="table-responsive">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Mois</th>
                            <th>Ventes</th>
                            <th>Annulées</th>
                            <th>CA Net</th>
                            <th>Encaissé</th>
                            <th>Charges</th>
                            <th>Résultat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {behaviorData.monthly?.map(m => (
                            <tr key={m.month}>
                              <td><strong>{m.monthName}</strong></td>
                              <td>{m.ventes_valid}</td>
                              <td className={m.ventes_cancelled > 0 ? 'text-danger' : ''}>{m.ventes_cancelled}</td>
                              <td>{fmtMoney(m.ca_net)}</td>
                              <td>{fmtMoney(m.encaisse)}</td>
                              <td className="text-danger">{fmtMoney(m.charges)}</td>
                              <td className={m.resultat >= 0 ? 'text-success' : 'text-danger'}>
                                {m.resultat >= 0 ? '+' : ''}{fmtMoney(m.resultat)}
                              </td>
                            </tr>
                          ))}
                          <tr className="total-row">
                            <td><strong>TOTAL</strong></td>
                            <td><strong>{behaviorData.monthly?.reduce((s, m) => s + m.ventes_valid, 0)}</strong></td>
                            <td className="text-danger"><strong>{behaviorData.monthly?.reduce((s, m) => s + m.ventes_cancelled, 0)}</strong></td>
                            <td><strong>{fmtMoney(behaviorData.ventes?.ca_net)}</strong></td>
                            <td><strong>{fmtMoney(behaviorData.ventes?.total_encaisse)}</strong></td>
                            <td className="text-danger"><strong>{fmtMoney(behaviorData.charges?.total)}</strong></td>
                            <td className={behaviorData.resultat?.benefice_brut >= 0 ? 'text-success' : 'text-danger'}>
                              <strong>{behaviorData.resultat?.benefice_brut >= 0 ? '+' : ''}{fmtMoney(behaviorData.resultat?.benefice_brut)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Recent Cancelled Sales ── */}
                  {behaviorData.recentCancelled?.length > 0 && (
                    <div className="data-usage-table">
                      <h4><FontAwesomeIcon icon={faTimesCircle} style={{ color: '#ef4444' }} /> Dernières Ventes Annulées</h4>
                      <div className="table-responsive">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>N° Vente</th>
                              <th>Client</th>
                              <th>Montant</th>
                              <th>Raison</th>
                              <th>Annulé par</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {behaviorData.recentCancelled.map((v, i) => (
                              <tr key={i}>
                                <td><strong>{v.sale_number || '-'}</strong></td>
                                <td>{v.customer}</td>
                                <td className="text-danger">{fmtMoney(v.amount)} FCFA</td>
                                <td><span className="cancellation-reason">{v.reason}</span></td>
                                <td>{v.cancelled_by}</td>
                                <td>{v.cancelled_at ? new Date(v.cancelled_at).toLocaleDateString('fr-FR') : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state"><p>Aucune donnée disponible</p></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gestion Access Modal */}
      {showGestionModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowGestionModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={gestionAction === 'grant' ? faToggleOn : faToggleOff} />
                {' '}{gestionAction === 'grant' ? 'Accorder' : 'Retirer'} l'accès Gestion
              </h2>
              <button className="close-btn" onClick={() => setShowGestionModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="gestion-info">
                <p><strong>Groupe :</strong> {selectedGroup.nom}</p>
                <p><strong>Statut actuel :</strong> 
                  <span className={`status-badge ${selectedGroup.gestion_access ? 'active' : 'inactive'}`}>
                    {selectedGroup.gestion_access ? ' Accès Gestion actif' : ' Pas d\'accès Gestion'}
                  </span>
                </p>
              </div>
              
              <div className="warning-box">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <p>
                  {gestionAction === 'grant' 
                    ? 'Accorder l\'accès Gestion permettra à ce groupe d\'utiliser les fonctionnalités de gestion avancées.'
                    : 'Retirer l\'accès Gestion désactivera les fonctionnalités de gestion avancées pour ce groupe.'
                  }
                </p>
              </div>

              <div className="form-group">
                <label>Raison de la demande *</label>
                <textarea
                  value={gestionReason}
                  onChange={(e) => setGestionReason(e.target.value)}
                  placeholder={gestionAction === 'grant' 
                    ? "Expliquez pourquoi ce groupe doit avoir accès à Gestion..."
                    : "Expliquez pourquoi l'accès Gestion doit être retiré..."
                  }
                  rows={3}
                  required
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowGestionModal(false)}>
                Annuler
              </button>
              <button 
                className={gestionAction === 'grant' ? 'btn-confirm-approve' : 'btn-confirm-reject'}
                onClick={submitGestionRequest}
                disabled={!gestionReason.trim()}
              >
                <FontAwesomeIcon icon={gestionAction === 'grant' ? faCheck : faTimes} />
                {' '}{gestionAction === 'grant' ? 'Accorder l\'accès' : 'Retirer l\'accès'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prestations Access Modal */}
      {showPrestationsModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowPrestationsModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={prestationsAction === 'grant' ? faToggleOn : faToggleOff} />
                {' '}{prestationsAction === 'grant' ? 'Accorder' : 'Retirer'} l'accès Prestations
              </h2>
              <button className="close-btn" onClick={() => setShowPrestationsModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="gestion-info">
                <p><strong>Groupe :</strong> {selectedGroup.nom}</p>
                <p><strong>Statut actuel :</strong> 
                  <span className={`status-badge ${selectedGroup.prestations_access ? 'active' : 'inactive'}`}>
                    {selectedGroup.prestations_access ? ' Accès Prestations actif' : ' Pas d\'accès Prestations'}
                  </span>
                </p>
              </div>
              
              <div className="warning-box">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <p>
                  {prestationsAction === 'grant' 
                    ? 'Accorder l\'accès Prestations permettra à ce groupe d\'utiliser les fonctionnalités de devis, factures et projets.'
                    : 'Retirer l\'accès Prestations désactivera les fonctionnalités de devis, factures et projets pour ce groupe.'
                  }
                </p>
              </div>

              <div className="form-group">
                <label>Raison de la demande *</label>
                <textarea
                  value={prestationsReason}
                  onChange={(e) => setPrestationsReason(e.target.value)}
                  placeholder={prestationsAction === 'grant' 
                    ? "Expliquez pourquoi ce groupe doit avoir accès aux Prestations..."
                    : "Expliquez pourquoi l'accès Prestations doit être retiré..."
                  }
                  rows={3}
                  required
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowPrestationsModal(false)}>
                Annuler
              </button>
              <button 
                className={prestationsAction === 'grant' ? 'btn-confirm-approve' : 'btn-confirm-reject'}
                onClick={submitPrestationsRequest}
                disabled={!prestationsReason.trim()}
              >
                <FontAwesomeIcon icon={prestationsAction === 'grant' ? faCheck : faTimes} />
                {' '}{prestationsAction === 'grant' ? 'Accorder l\'accès' : 'Retirer l\'accès'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default GroupsDashboard;
